import {
    Alert,
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    AlertIcon,
    Badge,
    Box,
    Button,
    Checkbox,
    HStack,
    Link,
    Select,
    SimpleGrid,
    Spinner,
    Text,
    Textarea,
    Tooltip,
    useDisclosure,
    useToast,
    VStack
} from '@chakra-ui/react';
import React, { useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MAX_LEVEL, MIN_LEVEL } from '../../models/user/dto/user.dto';
import ErrorState from '../../components/states/ErrorState';
import PageLayout from '../../components/page-layout';
import PageCard from '../../components/page-card';
import { translate, TranslationKeys } from '../../i18n';
import { GameDto, GameRankedMode, GameStatus, GameType } from '../../models/game/dto/game.dto';
import {
    GameCancellationPolicy,
    GameCancellationReason,
} from '../../models/payment/dto/game-cancellation.dto';
import { PaymentIntentStatus } from '../../models/payment/dto/payment-intent.dto';
import AccessCredentialsCard from './components/AccessCredentialsCard';
import { AddUserToGameModal } from './components/AddUserToGameModal';
import AdminCommentsSection from './components/AdminCommentsSection';
import { EditGameCourtModal } from './components/EditGameCourtModal';
import { EditGameTimesModal } from './components/EditGameTimesModal';
import GameOrdersCard from './components/GameOrdersCard';
import ParticipantsCard from './components/ParticipantsCard';
import PaymentsTable from './components/PaymentsTable';
import RefundsCard from './components/RefundsCard';
import ReplacementRequestsCard from './components/ReplacementRequestsCard';
import { useCancelGame } from './hooks/useCancelGame';
import { useGenerateReplacementLink } from './hooks/useGenerateReplacementLink';
import { useGetGame } from './hooks/useGetGame';
import { useGetGameBookingCode } from './hooks/useGetGameBookingCode';
import { useGetGameOrders } from './hooks/useGetGameOrders';
import { useToggleFullyPaid } from './hooks/useToggleFullyPaid';
import { useIsOrgManager } from '../auth/roles';
import { useSelectedOrganization } from '../organization/hooks/useSelectedOrganization';

export default function GameDetailsPage() {
    const location = useLocation();
    const gameId = useMemo(() => {
        // Validate the RAW parameter, not a parsed result: `Number.parseInt`
        // discards a trailing suffix, so `?game=12abc` and `?game=12.5` would
        // both silently open game 12. Canonical positive decimals only — the
        // same accept set `api/src/libs/http/parse-numeric-id.ts` applies to the
        // matching path params, so the client and the API agree on what an id
        // is. `0` is excluded deliberately: no row has it, and `useGetGame` is
        // gated `enabled: !!gameId`, so a `0` that got through would leave the
        // query disabled and the page on the spinner this branch exists to
        // remove.
        const raw = (new URLSearchParams(location.search).get('game') ?? '').trim();
        return /^[1-9]\d*$/.test(raw) ? Number(raw) : NaN;
    }, [location.search]);
    const isValidGameId = Number.isInteger(gameId);
    const navigate = useNavigate();
    const { selectedOrganization } = useSelectedOrganization();
    // Coaches get a read-only view: payment/refund/order data and every
    // mutation are admin-only on the API (403), so the sections and buttons
    // are not rendered — and the admin-only queries never fire.
    const isOrgManager = useIsOrgManager();
    const { data: game, isLoading, error, refetch } = useGetGame(gameId);
    const { data: bookingCodeData } = useGetGameBookingCode(gameId);
    const { data: orders, isLoading: ordersLoading, error: ordersError } = useGetGameOrders(gameId, { enabled: isOrgManager });
    const toggleFullyPaidMutation = useToggleFullyPaid();
    const replacementLinkMutation = useGenerateReplacementLink();
    const cancelGameMutation = useCancelGame();
    const { isOpen: isEditTimesOpen, onOpen: onEditTimesOpen, onClose: onEditTimesClose } = useDisclosure();
    const { isOpen: isEditCourtOpen, onOpen: onEditCourtOpen, onClose: onEditCourtClose } = useDisclosure();
    const { isOpen: isAddUserOpen, onOpen: onAddUserOpen, onClose: onAddUserClose } = useDisclosure();
    const cancelRefundDisclosure = useDisclosure();
    const cancelNoRefundDisclosure = useDisclosure();
    const cancelWalletDisclosure = useDisclosure();
    const toast = useToast();

    if (!isValidGameId) {
        // Reached when the `game` URL param is missing/garbled (a bookmark of
        // `#/admin/gameDetails`, a shared link whose hash query was dropped, a
        // hand-typed path). `useGetGame` is gated `enabled: !!gameId` and
        // `!!NaN` is false, so react-query leaves the query in `status:
        // 'loading'` forever — without this branch the page renders the spinner
        // below indefinitely, promising a load that can never happen. Mirrors
        // `CourtDetailsPage`'s `isValidCourtId` card.
        return (
            <PageCard showBackButton title={translate('game.not_found')}>
                <Box px={6} py={10}>
                    <Box mb={4}>{translate('game.not_found_description')}</Box>
                    <Button colorScheme="brand" onClick={() => navigate('/admin/calendar')}>
                        {translate('tabs.calendar')}
                    </Button>
                </Box>
            </PageCard>
        );
    }

    if (isLoading && !game) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Spinner size="lg" />
            </Box>
        );
    }

    if (error || !game) {
        return (
            <ErrorState message={translate('game.error_loading_details')} onRetry={refetch} />
        );
    }

    // Buttons hidden on COMPLETED. Visible on UPCOMING and CANCELED — same-policy
    // retry is idempotent (partial-failure recovery), opposite-policy throws
    // 409 ConflictException via the service and surfaces as an error toast.
    const canCancel = game.status !== GameStatus.COMPLETED;

    return (
        <PageLayout>
            {game.replacedFromGameId && (
                <Alert status="info" borderRadius="md" mb={4}>
                    <AlertIcon />
                    <Text>
                        {translate('game.replaced_from_game')}{' '}
                        <Link
                            color="blue.400"
                            fontWeight="bold"
                            href={`#/admin/gameDetails?game=${game.replacedFromGameId}`}
                        >
                            #{game.replacedFromGameId}
                        </Link>
                    </Text>
                </Alert>
            )}

            <PageCard
                showBackButton
                title={translate('game.details')}>
                <GameInformation
                    game={game}
                    bookingCode={bookingCodeData?.bookingCode}
                    toggleFullyPaidMutation={isOrgManager ? toggleFullyPaidMutation : undefined}
                    onCancelRefund={isOrgManager && canCancel ? cancelRefundDisclosure.onOpen : undefined}
                    onCancelNoRefund={isOrgManager && canCancel ? cancelNoRefundDisclosure.onOpen : undefined}
                    onCancelWallet={isOrgManager && canCancel ? cancelWalletDisclosure.onOpen : undefined}
                    onEditTimes={isOrgManager ? onEditTimesOpen : undefined}
                    onEditCourt={isOrgManager ? onEditCourtOpen : undefined}
                />
            </PageCard>

            <PageCard
                title={`${translate('game.participants')} (${game.participants.length}/${game.capacity})`}
                rightHeader={
                    isOrgManager && game.participants.length < game.capacity && game.status !== GameStatus.CANCELED ? (
                        <Button
                            size="sm"
                            colorScheme="green"
                            variant="outline"
                            onClick={onAddUserOpen}
                        >
                            {translate('game.add_user')}
                        </Button>
                    ) : null
                }
            >
                <ParticipantsCard game={game} />
            </PageCard>

            {/* Self-hides when the org has no access-control integration.
                Admin-only: the credentials endpoint returns raw door PINs and
                rejects coach memberships server-side. */}
            {isOrgManager && (
                <AccessCredentialsCard
                    organizationId={game.court?.location?.organizationId ?? selectedOrganization?.id}
                    gameId={game.id}
                />
            )}

            {/* Financial sections are admin-only (the API 403s the reads and
                every action for coach roles). */}
            {isOrgManager && (
                <>
                    <PageCard title={translate('game.payments')}>
                        <PaymentsTable game={game} />
                    </PageCard>

                    <PageCard title={`${translate('game.replacement_requests')} (${game.replacementRequests?.length ?? 0})`}>
                        <ReplacementRequestsCard
                            replacementRequests={game.replacementRequests ?? []}
                            onGenerateLink={
                                new Date(game.startTime) > new Date() && game.status !== GameStatus.COMPLETED
                                    ? () => replacementLinkMutation.mutate(game.id)
                                    : undefined
                            }
                            isGeneratingLink={replacementLinkMutation.isLoading}
                        />
                    </PageCard>

                    <PageCard title={`${translate('game.refunds_title')} (${game.refunds?.length ?? 0})`}>
                        <RefundsCard refunds={game.refunds ?? []} />
                    </PageCard>

                    <PageCard title={`${translate('game.orders')} (${orders?.length ?? 0})`}>
                        <GameOrdersCard orders={orders} isLoading={ordersLoading} error={ordersError} />
                    </PageCard>
                </>
            )}

            {/* Internal notes stay membership-level on the API — coaches can
                read and add them. */}
            <PageCard title={`${translate('admin_comments.title')}`}>
                <AdminCommentsSection game={game} />
            </PageCard>

            <EditGameTimesModal
                isOpen={isEditTimesOpen}
                onClose={onEditTimesClose}
                game={game}
            />

            <EditGameCourtModal
                isOpen={isEditCourtOpen}
                onClose={onEditCourtClose}
                game={game}
            />

            {/* Admin-only, and the gate has to be on the MOUNT, not just on the
                button that opens it. This modal calls `useGetUsers` at mount —
                not on open — so while it was rendered unconditionally, every
                coach opening any match from Calendrier silently downloaded the
                organization's entire user roster (names, emails, levels) for a
                button they cannot see. `GET /users` is admin-gated server-side
                as of the same change; the two siblings above are safe because
                they only read the unguarded court list. */}
            {isOrgManager && (
                <AddUserToGameModal
                    isOpen={isAddUserOpen}
                    onClose={onAddUserClose}
                    game={game}
                />
            )}

            <CancelGameDialog
                policy={GameCancellationPolicy.REFUND}
                isOpen={cancelRefundDisclosure.isOpen}
                onClose={cancelRefundDisclosure.onClose}
                game={game}
                isLoading={cancelGameMutation.isLoading}
                onConfirm={(input) => cancelGameMutation.mutate({ gameId: game.id, ...input })}
            />
            <CancelGameDialog
                policy={GameCancellationPolicy.NO_REFUND}
                isOpen={cancelNoRefundDisclosure.isOpen}
                onClose={cancelNoRefundDisclosure.onClose}
                game={game}
                isLoading={cancelGameMutation.isLoading}
                onConfirm={(input) => cancelGameMutation.mutate({ gameId: game.id, ...input })}
            />
            <CancelGameDialog
                policy={GameCancellationPolicy.WALLET_CREDIT}
                isOpen={cancelWalletDisclosure.isOpen}
                onClose={cancelWalletDisclosure.onClose}
                game={game}
                isLoading={cancelGameMutation.isLoading}
                onConfirm={(input) => cancelGameMutation.mutate({ gameId: game.id, ...input })}
            />
        </PageLayout>
    );
}

interface GameInformationProps {
    game: GameDto;
    bookingCode?: string;
    // Admin-only affordances. The parent passes `undefined` for COACH members
    // (whose API calls these endpoints would 403), so EVERY one of these must be
    // optional here and presence-gated at its use site. Declaring one required
    // does not fail the build — `strictNullChecks` is off (tsconfig.json:22) —
    // it renders a white screen for coaches instead, because a required prop
    // read during render (`toggleFullyPaidMutation.isLoading`) throws with no
    // ErrorBoundary anywhere in the app to catch it.
    toggleFullyPaidMutation?: ReturnType<typeof useToggleFullyPaid>;
    onCancelRefund?: () => void;
    onCancelNoRefund?: () => void;
    onCancelWallet?: () => void;
    onEditTimes?: () => void;
    onEditCourt?: () => void;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const formatDate = (date: Date) => {
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = MONTHS[d.getMonth()];
    const year = d.getFullYear();
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    return `${day} ${month} ${year} - ${hours}:${minutes}`;
};
// Exported for tests only: the coach-vs-admin prop shape is what broke this
// page for coaches once already (see GameDetailsPage.test.ts).
export const GameInformation: React.FC<GameInformationProps> = ({
    game,
    bookingCode,
    toggleFullyPaidMutation,
    onCancelRefund,
    onCancelNoRefund,
    onCancelWallet,
    onEditTimes,
    onEditCourt,
}) => {
    // Both halves must hold before the fully-paid checkbox may fire a request:
    // the caller supplied the mutation (admin, not coach) AND the game is not
    // comped (the API 400s on those — DASHBOARD-2). Derived once so the
    // `onChange` handler and the read-only flag can never disagree.
    const canToggleFullyPaid = Boolean(toggleFullyPaidMutation) && !game.isComped;

    const getGameTypeBadge = (type: GameType) => {
        const color = type === GameType.PRIVATE ? 'purple' : 'blue';
        const text = type === GameType.PRIVATE
            ? translate('game.private')
            : translate('game.public');
        return <Badge colorScheme={color}>{text}</Badge>;
    };

    const getGameStatusBadge = (status: GameStatus) => {
        let color = 'gray';
        let text = status;

        switch (status) {
            case GameStatus.UPCOMING:
                color = 'green';
                text = translate('game.upcoming');
                break;
            case GameStatus.COMPLETED:
                color = 'blue';
                text = translate('game.completed');
                break;
            case GameStatus.CANCELED:
                color = 'red';
                text = translate('game.canceled');
                break;
        }

        return <Badge colorScheme={color}>{text}</Badge>;
    };

    const getGameRankedBadge = (rankedMode: GameRankedMode) => {
        const color = rankedMode === GameRankedMode.RANKED ? 'orange' : 'gray';
        const text = rankedMode === GameRankedMode.RANKED
            ? translate('game.ranked')
            : translate('game.unranked');
        return <Badge colorScheme={color}>{text}</Badge>;
    };

    /**
     * A band that admits every player is NOT a restriction, and printing it as
     * one misleads the operator (D122).
     *
     * The columns default to 0 / 10 and player levels are clamped to
     * [MIN_LEVEL, MAX_LEVEL], so every game the dashboard itself creates
     * (`CreateAdminBookingModal` sends no band at all) arrives as 0 / 10. The
     * truthiness chain below then fell through the first two arms — `0` is
     * falsy — and rendered `<= 10`, which reads as "level 10 and under only" on
     * a game that has never restricted anybody.
     */
    const formatLevelRange = () => {
        const min = game.minLevel ?? null;
        const max = game.maxLevel ?? null;
        const hasFloor = min != null && min > MIN_LEVEL;
        const hasCeiling = max != null && max < MAX_LEVEL;
        if (hasFloor && hasCeiling) {
            return `${min} - ${max}`;
        } else if (hasFloor) {
            return `${min}+`;
        } else if (hasCeiling) {
            return `<= ${max}`;
        }
        return '-';
    };

    return (
        <VStack spacing={4} align="stretch">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} padding={6}>
                <VStack align="start" spacing={3}>
                    <InfoRow
                        label={translate('game.organizer')}
                        value={`${game.organiser.firstName} ${game.organiser.lastName}`}
                    />
                    {game.bookedByOperatorId && (
                        <Text fontSize="sm" color="gray.600">
                            Réservé par opérateur (ID: {game.bookedByOperatorId})
                        </Text>
                    )}
                    <InfoRow
                        label={translate('game.start_time')}
                        value={formatDate(game.startTime)}
                    />
                    <HStack spacing={2} flexWrap="wrap">
                        {onEditTimes && (
                            <Button
                                size="sm"
                                colorScheme="brand"
                                variant="outline"
                                onClick={onEditTimes}
                            >
                                {translate('game.edit_times')}
                            </Button>
                        )}
                        {(() => {
                            const isCanceled = game.status === GameStatus.CANCELED;
                            const existingPolicy = game.cancellationPolicy;
                            const isRefundDisabled = isCanceled && !!existingPolicy && existingPolicy !== 'REFUND';
                            const isNoRefundDisabled = isCanceled && !!existingPolicy && existingPolicy !== 'NO_REFUND';
                            const isWalletDisabled = isCanceled && !!existingPolicy && existingPolicy !== 'WALLET_CREDIT';
                            const disabledTooltip = existingPolicy
                                ? translate('game.cancel_disabled_tooltip' as TranslationKeys)
                                : '';

                            return (
                                <>
                                    {isRefundDisabled ? (
                                        <Tooltip label={disabledTooltip}>
                                            <Button size="sm" colorScheme="red" variant="outline" isDisabled>
                                                {translate('game.cancel_and_refund')}
                                            </Button>
                                        </Tooltip>
                                    ) : onCancelRefund ? (
                                        <Button size="sm" colorScheme="red" variant="outline" onClick={onCancelRefund}>
                                            {translate('game.cancel_and_refund')}
                                        </Button>
                                    ) : null}

                                    {isNoRefundDisabled ? (
                                        <Tooltip label={disabledTooltip}>
                                            <Button size="sm" colorScheme="red" variant="solid" isDisabled>
                                                {translate('game.cancel_without_refund')}
                                            </Button>
                                        </Tooltip>
                                    ) : onCancelNoRefund ? (
                                        <Button size="sm" colorScheme="red" variant="solid" onClick={onCancelNoRefund}>
                                            {translate('game.cancel_without_refund')}
                                        </Button>
                                    ) : null}

                                    {isWalletDisabled ? (
                                        <Tooltip label={disabledTooltip}>
                                            <Button size="sm" colorScheme="orange" variant="solid" isDisabled>
                                                {translate('game.cancel_and_credit_wallets')}
                                            </Button>
                                        </Tooltip>
                                    ) : onCancelWallet ? (
                                        <Button size="sm" colorScheme="orange" variant="solid" onClick={onCancelWallet}>
                                            {translate('game.cancel_and_credit_wallets')}
                                        </Button>
                                    ) : null}
                                </>
                            );
                        })()}
                    </HStack>
                    <InfoRow
                        label={translate('game.duration')}
                        value={`${Math.round(game.duration / 60000)} ${translate('game.minutes')}`}
                    />
                    <InfoRow
                        label={translate('game.court')}
                        value={game.court.name}
                    />
                    {onEditCourt && (
                        <Button
                            size="sm"
                            colorScheme="brand"
                            variant="outline"
                            onClick={onEditCourt}
                        >
                            {translate('game.edit_court')}
                        </Button>
                    )}
                </VStack>

                <VStack align="start" spacing={3}>
                    <HStack>
                        <Text fontWeight="bold">{translate('game.type')}:</Text>
                        {getGameTypeBadge(game.type)}
                    </HStack>
                    <HStack>
                        <Text fontWeight="bold">{translate('game.status')}:</Text>
                        {getGameStatusBadge(game.status)}
                    </HStack>
                    <HStack>
                        <Text fontWeight="bold">{translate('game.ranked_mode')}:</Text>
                        {getGameRankedBadge(game.rankedMode)}
                    </HStack>
                    {bookingCode && (
                        <InfoRow
                            label={translate('game.booking_code')}
                            value={bookingCode}
                        />
                    )}
                    <InfoRow
                        label={translate('game.capacity')}
                        value={`${game.participants.length} / ${game.capacity}`}
                    />
                    <InfoRow
                        label={translate('game.level_range')}
                        value={formatLevelRange()}
                    />

                    {/* Two reasons this control can be non-actionable, both of
                        which keep the STATUS visible and only remove the click:

                        1. Coaches — `games/:id/toggle-fully-paid` is
                           hasOrganizationAdminAccess, so a coach's call would 403.
                        2. Comped games (MYSPORTSET DASHBOARD-2) — the API rejects
                           the toggle outright with a 400, because a comped game
                           holds fullyPaid=true as an invariant ("organiser owes
                           nothing"); see game.service.ts:120 and
                           recompute-game-fully-paid.ts:23. Offering the checkbox
                           anyway produced a guaranteed-failing request: 340 events
                           and 11 operators hitting an error toast that told them
                           nothing they could act on from here. The tooltip now
                           carries the API's own remedy — change the game type. */}
                    <HStack>
                        <Tooltip
                            label={translate('game.fully_paid_comped_locked')}
                            isDisabled={!game.isComped}
                            shouldWrapChildren
                        >
                            <Checkbox
                                isChecked={game.fullyPaid}
                                onChange={
                                    canToggleFullyPaid
                                        ? () => toggleFullyPaidMutation.mutate(game.id)
                                        : undefined
                                }
                                isReadOnly={!canToggleFullyPaid}
                                isDisabled={toggleFullyPaidMutation?.isLoading}
                                colorScheme="green"
                            >
                                <Text fontWeight="bold">{translate('game.fully_paid')}</Text>
                            </Checkbox>
                        </Tooltip>
                        {toggleFullyPaidMutation?.isLoading && (
                            <Spinner size="sm" color="brand.500" />
                        )}
                    </HStack>
                </VStack>
            </SimpleGrid>
        </VStack>
    );
};

interface InfoRowProps {
    label: string;
    value: string;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => (
    <HStack>
        <Text fontWeight="bold" minWidth="120px" whiteSpace="nowrap">{label}:</Text>
        <Text whiteSpace="nowrap">{value}</Text>
    </HStack>
);

interface CancelGameDialogProps {
    policy: GameCancellationPolicy;
    isOpen: boolean;
    onClose: () => void;
    game: GameDto;
    isLoading: boolean;
    onConfirm: (input: {
        policy: GameCancellationPolicy;
        reason?: GameCancellationReason;
        reasonText?: string;
    }) => void;
}

const POLICY_TITLE_KEY: Record<GameCancellationPolicy, TranslationKeys> = {
    [GameCancellationPolicy.REFUND]: 'game.cancel_and_refund' as TranslationKeys,
    [GameCancellationPolicy.NO_REFUND]: 'game.cancel_without_refund' as TranslationKeys,
    [GameCancellationPolicy.WALLET_CREDIT]: 'game.cancel_and_credit_wallets' as TranslationKeys,
};

const POLICY_BODY_KEY: Record<GameCancellationPolicy, TranslationKeys> = {
    [GameCancellationPolicy.REFUND]: 'game.cancel_and_refund_dialog_body' as TranslationKeys,
    [GameCancellationPolicy.NO_REFUND]: 'game.cancel_without_refund_dialog_body' as TranslationKeys,
    [GameCancellationPolicy.WALLET_CREDIT]: 'game.cancel_and_credit_wallets_dialog_body' as TranslationKeys,
};

const POLICY_COLOR: Record<GameCancellationPolicy, string> = {
    [GameCancellationPolicy.REFUND]: 'red',
    [GameCancellationPolicy.NO_REFUND]: 'red',
    [GameCancellationPolicy.WALLET_CREDIT]: 'orange',
};

const CancelGameDialog: React.FC<CancelGameDialogProps> = ({
    policy,
    isOpen,
    onClose,
    game,
    isLoading,
    onConfirm,
}) => {
    const cancelRef = useRef<HTMLButtonElement>(null);
    const [reason, setReason] = useState<GameCancellationReason | undefined>(undefined);
    const [reasonText, setReasonText] = useState<string>('');

    const handleClose = () => {
        setReason(undefined);
        setReasonText('');
        onClose();
    };

    const confirmDisabled =
        reason === GameCancellationReason.OTHER && !reasonText.trim();

    return (
        <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={handleClose}
            size="lg"
        >
            <AlertDialogOverlay>
                <AlertDialogContent>
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">
                        {translate(POLICY_TITLE_KEY[policy])}
                    </AlertDialogHeader>
                    <AlertDialogBody>
                        <VStack align="stretch" spacing={3}>
                            <Text>{translate(POLICY_BODY_KEY[policy])}</Text>
                            <PendingPaymentIntentSummary game={game} />
                            <Box>
                                <Text fontSize="sm" fontWeight="semibold" mb={1}>
                                    {translate('game.cancel_reason_label')}
                                </Text>
                                <Select
                                    value={reason ?? ''}
                                    onChange={(e) =>
                                        setReason(
                                            (e.target.value || undefined) as
                                                | GameCancellationReason
                                                | undefined,
                                        )
                                    }
                                    placeholder={translate('game.cancel_reason_placeholder')}
                                    size="sm"
                                >
                                    <option value={GameCancellationReason.NO_SHOW}>
                                        {translate('game.cancel_reason_no_show')}
                                    </option>
                                    <option value={GameCancellationReason.WEATHER}>
                                        {translate('game.cancel_reason_weather')}
                                    </option>
                                    <option value={GameCancellationReason.VENUE_ISSUE}>
                                        {translate('game.cancel_reason_venue_issue')}
                                    </option>
                                    <option value={GameCancellationReason.ADMIN_ERROR}>
                                        {translate('game.cancel_reason_admin_error')}
                                    </option>
                                    <option value={GameCancellationReason.OTHER}>
                                        {translate('game.cancel_reason_other')}
                                    </option>
                                </Select>
                            </Box>
                            {reason === GameCancellationReason.OTHER && (
                                <Textarea
                                    placeholder={translate('game.cancel_reason_text_placeholder')}
                                    value={reasonText}
                                    onChange={(e) => setReasonText(e.target.value)}
                                    size="sm"
                                    maxLength={2000}
                                />
                            )}
                        </VStack>
                    </AlertDialogBody>
                    <AlertDialogFooter>
                        <Button ref={cancelRef} onClick={handleClose}>
                            {translate('common.cancel')}
                        </Button>
                        <Button
                            colorScheme={POLICY_COLOR[policy]}
                            onClick={() => {
                                onConfirm({
                                    policy,
                                    reason,
                                    reasonText:
                                        reason === GameCancellationReason.OTHER
                                            ? reasonText.trim()
                                            : undefined,
                                });
                                handleClose();
                            }}
                            ml={3}
                            isLoading={isLoading}
                            isDisabled={confirmDisabled}
                        >
                            {translate(POLICY_TITLE_KEY[policy])}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
    );
};

const PendingPaymentIntentSummary: React.FC<{ game: GameDto }> = ({ game }) => {
    const pis = game.paymentIntents ?? [];

    // Skip rules mirror the API service cancel-game-as-admin.ts:
    // - 'pos_*' / 'wallet_pos_*' = Nextore POS (cashier handles refund)
    // - 'manual_*' paymentMethodType = collected at venue (admin handles refund)
    const isPos = (id: string) => id.startsWith('pos_') || id.startsWith('wallet_pos_');
    const isManual = (pi: typeof pis[number]) => pi.paymentMethodType?.startsWith('manual_');

    const succeededRefundable = pis.filter(
        (pi) => pi.status === PaymentIntentStatus.succeeded && !isPos(pi.id) && !isManual(pi),
    );
    const succeededPos = pis.filter(
        (pi) => pi.status === PaymentIntentStatus.succeeded && isPos(pi.id),
    );
    const succeededManual = pis.filter(
        (pi) => pi.status === PaymentIntentStatus.succeeded && isManual(pi),
    );
    const requiresCapture = pis.filter(
        (pi) => pi.status === PaymentIntentStatus.requires_capture,
    );
    const payLater = pis.filter(
        (pi) => pi.status === PaymentIntentStatus.pay_later,
    );

    const totalRefundableCents = succeededRefundable.reduce(
        (sum, pi) => sum + (pi.amount ?? 0),
        0,
    );
    const currency = (succeededRefundable[0]?.currency ?? 'eur').toUpperCase();
    const totalRefundableFormatted = `${(totalRefundableCents / 100).toFixed(2)} ${currency}`;

    if (
        succeededRefundable.length === 0 &&
        succeededPos.length === 0 &&
        succeededManual.length === 0 &&
        requiresCapture.length === 0 &&
        payLater.length === 0
    ) {
        return null;
    }

    return (
        <Box bg="gray.50" borderRadius="md" p={3}>
            <Text fontSize="sm" fontWeight="semibold" mb={1}>
                {translate('game.cancel_dialog_pi_summary_title')}
            </Text>
            <VStack align="start" spacing={1}>
                {succeededRefundable.length > 0 && (
                    <Text fontSize="xs">
                        • {succeededRefundable.length} ×{' '}
                        {translate('game.cancel_dialog_succeeded_label')} (
                        {totalRefundableFormatted})
                    </Text>
                )}
                {requiresCapture.length > 0 && (
                    <Text fontSize="xs">
                        • {requiresCapture.length} ×{' '}
                        {translate('game.cancel_dialog_requires_capture_label')}
                    </Text>
                )}
                {payLater.length > 0 && (
                    <Text fontSize="xs">
                        • {payLater.length} × {translate('game.cancel_dialog_pay_later_label')}
                    </Text>
                )}
                {succeededPos.length > 0 && (
                    <Text fontSize="xs">
                        • {succeededPos.length} × {translate('game.cancel_dialog_pos_label')}
                    </Text>
                )}
                {succeededManual.length > 0 && (
                    <Text fontSize="xs">
                        • {succeededManual.length} × {translate('game.cancel_dialog_manual_label')}
                    </Text>
                )}
            </VStack>
        </Box>
    );
};
